import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import KundeUndProfil from '../person/KundeUndProfil';
import {
  LEERER_WERT,
  fehlendeFelderSatz,
  fehlendeKundenfelder,
  fehlendeSchuelerfelder,
  gewaehlteProfilId,
  type KundeUndProfilWert,
} from '../../lib/api/stammdaten';
import {
  trialRequestsApi,
  type ResolveTrialRequestMergePayload,
  type TrialRequestItem,
} from '../../lib/trialRequests';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: TrialRequestItem | null;
  onResolved: (updated: TrialRequestItem) => void;
};

/**
 * „Datensatz verknüpfen" — Kunde verknüpfen, Schülerprofil zuweisen.
 *
 * Aus dem Kundentest (T-0006): Der Dialog stellte einen Suchschlitz über alle
 * Schülerprofile hin und verlangte beim Neuanlegen ausgerechnet eine **andere**
 * E-Mail als die der Anfrage — also genau die, die man verknüpfen wollte.
 * Deshalb war beim Anlegen nie klar, um wen es geht: man legte zwangsläufig
 * jemand anderen an.
 *
 * Jetzt wird zuerst der Kunde bestimmt (vorgeschlagen, wenn die E-Mail bekannt
 * ist) und dann sein Schülerprofil gewählt.
 */
export default function TrialRequestMergeWizardModal({
  open,
  onOpenChange,
  request,
  onResolved,
}: Readonly<Props>) {
  const [wert, setWert] = useState<KundeUndProfilWert>(LEERER_WERT);
  const [pruefen, setPruefen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setWert(LEERER_WERT);
    setPruefen(false);
  }, [open, request?.id]);

  const schliessen = (offen: boolean) => {
    onOpenChange(offen);
    if (!offen) {
      setWert(LEERER_WERT);
      setPruefen(false);
    }
  };

  const auftrag = (): ResolveTrialRequestMergePayload | null => {
    const bestehenderKunde = wert.personId !== null;
    if (wert.profilWahl.art === 'neu') {
      const luecken = fehlendeSchuelerfelder(wert.schueler, wert.kunde.phone, wert.kunde.email);
      if (luecken.length > 0) {
        toast.error(`Schülerprofil: ${fehlendeFelderSatz(luecken)}`);
        return null;
      }
    }
    if (!bestehenderKunde) {
      const luecken = fehlendeKundenfelder(wert.kunde);
      if (luecken.length > 0) {
        toast.error(`Kunde: ${fehlendeFelderSatz(luecken)}`);
        return null;
      }
      return {
        mode: 'create_new',
        kunde: wert.kunde,
        schueler: wert.profilWahl.art === 'neu' ? wert.schueler : undefined,
      };
    }
    if (wert.profilWahl.art === 'neu') {
      // Die Kundendaten gehen mit: Was am bestehenden Kunden geändert wurde, soll
      // nicht verloren gehen (T-0314).
      return { mode: 'neues_profil', personId: wert.personId, kunde: wert.kunde, schueler: wert.schueler };
    }
    const subprofileId = gewaehlteProfilId(wert);
    if (!subprofileId) {
      toast.error('Bitte ein Schülerprofil wählen');
      return null;
    }
    return { mode: 'link_existing', subprofileId, kunde: wert.kunde };
  };

  const absenden = async () => {
    if (!request) return;
    setPruefen(true);
    const payload = auftrag();
    if (!payload) return;
    setSaving(true);
    try {
      const aktualisiert = await trialRequestsApi.resolveMerge(request.id, payload);
      toast.success('Anfrage verknüpft');
      onResolved(aktualisiert);
      schliessen(false);
    } catch (e: any) {
      // Der Server prüft dieselben Pflichtfelder; seine Meldung ist die genauere.
      toast.error(e?.message || 'Datensatz konnte nicht verknüpft werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={schliessen}>
      <DialogContent size="lg" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Anfrage verknüpfen</DialogTitle>
          <DialogDescription>
            Erst der Kunde, dann das Schülerprofil — beides kann bestehen oder neu sein.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <div className="font-medium">
            {request?.vorname} {request?.nachname}
          </div>
          <div className="text-xs text-muted-foreground">
            {request?.email || '—'} · {request?.telefon || '—'}
          </div>
        </div>

        <KundeUndProfil
          wert={wert}
          onChange={setWert}
          standortId={request?.standort_id ?? null}
          pruefen={pruefen}
          ausAnfrage={{
            firstName: request?.vorname || '',
            lastName: request?.nachname || '',
            email: request?.email || '',
            phone: request?.telefon || '',
          }}
        />

        <DialogFooter>
          <Button variant="outline" onClick={() => schliessen(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void absenden()} disabled={saving}>
            {wert.personId !== null ? 'Verknüpfen' : 'Kunden anlegen und verknüpfen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
